import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { PlusCircle, Trash2, ArrowRight, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  ReferenceListDependency,
  ReferenceListDependencyView,
  ReferenceListUsageView
} from "@/entities/all";

export default function ReferenceListDependencies({ listId, listName, onManageLinks }) {
  const [dependencies, setDependencies] = useState([]);
  const [allLists, setAllLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDep, setSelectedDep] = useState(null);
  const [addForm, setAddForm] = useState({ role: 'parent', list_id: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (listId) loadDependencies();
  }, [listId]);

  const loadDependencies = async () => {
    setIsLoading(true);
    try {
      const deps = await ReferenceListDependencyView.list();
      const relevant = deps.filter(
        d => String(d.parent_list_id) === String(listId) || String(d.child_list_id) === String(listId)
      );
      setDependencies(relevant);
    } catch (error) {
      console.error('Error loading dependencies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllLists = async () => {
    try {
      const lists = await ReferenceListUsageView.list('list_name');
      setAllLists(lists.filter(l => String(l.id) !== String(listId)));
    } catch (error) {
      console.error('Error loading lists:', error);
    }
  };

  const handleOpenAddModal = () => {
    loadAllLists();
    setAddForm({ role: 'parent', list_id: '', description: '' });
    setIsAddModalOpen(true);
  };

  const handleAddDependency = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data = addForm.role === 'parent'
        ? { parent_list_id: parseInt(listId), child_list_id: parseInt(addForm.list_id), description: addForm.description || null }
        : { parent_list_id: parseInt(addForm.list_id), child_list_id: parseInt(listId), description: addForm.description || null };
      await ReferenceListDependency.create(data);
      setIsAddModalOpen(false);
      loadDependencies();
    } catch (error) {
      console.error('Error creating dependency:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDependency = async () => {
    if (!selectedDep) return;
    try {
      await ReferenceListDependency.delete(selectedDep.id);
      setIsDeleteDialogOpen(false);
      setSelectedDep(null);
      loadDependencies();
    } catch (error) {
      console.error('Error deleting dependency:', error);
    }
  };

  const parentDeps = dependencies.filter(d => String(d.child_list_id) === String(listId));
  const childDeps = dependencies.filter(d => String(d.parent_list_id) === String(listId));

  return (
    <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-none">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold">Dependencies</CardTitle>
        <button
          onClick={handleOpenAddModal}
          aria-label="Add Dependency"
          className="flex items-center justify-center gap-2 h-9 w-9 md:w-auto md:px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
        >
          <PlusCircle size={24} />
          <span className="hidden md:inline">Add Dependency</span>
        </button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-slate-200 rounded w-full"></div>
          </div>
        ) : dependencies.length === 0 ? (
          <p className="text-sm text-muted-foreground">No dependencies configured for this list.</p>
        ) : (
          <div className="space-y-4">
            {parentDeps.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-500 mb-2">Parent Lists (this list depends on)</h4>
                <div className="space-y-2">
                  {parentDeps.map(dep => (
                    <div key={dep.id} className="flex items-center justify-between rounded-lg border p-3">
                      <div className="flex items-center gap-3">
                        <Link
                          to={createPageUrl("reference-list-values") + `?id=${dep.parent_list_id}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {dep.parent_list_name}
                        </Link>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">{listName}</span>
                        {dep.description && (
                          <span className="text-xs text-muted-foreground">({dep.description})</span>
                        )}
                        <Badge variant="secondary">{dep.link_count} links</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {onManageLinks ? (
                          <Button size="sm" variant="ghost" onClick={() => onManageLinks(dep.id)}>
                            <LinkIcon className="w-4 h-4 mr-1" />
                            Manage Links
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={createPageUrl("reference-list-values") + `?id=${listId}&dep=${dep.id}`}>
                              <LinkIcon className="w-4 h-4 mr-1" />
                              Manage Links
                            </Link>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setSelectedDep(dep); setIsDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {childDeps.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-500 mb-2">Child Lists (depend on this list)</h4>
                <div className="space-y-2">
                  {childDeps.map(dep => (
                    <div key={dep.id} className="flex items-center justify-between rounded-lg border p-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-600">{listName}</span>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <Link
                          to={createPageUrl("reference-list-values") + `?id=${dep.child_list_id}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {dep.child_list_name}
                        </Link>
                        {dep.description && (
                          <span className="text-xs text-muted-foreground">({dep.description})</span>
                        )}
                        <Badge variant="secondary">{dep.link_count} links</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {onManageLinks ? (
                          <Button size="sm" variant="ghost" onClick={() => onManageLinks(dep.id)}>
                            <LinkIcon className="w-4 h-4 mr-1" />
                            Manage Links
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={createPageUrl("reference-list-values") + `?id=${dep.child_list_id}&dep=${dep.id}`}>
                              <LinkIcon className="w-4 h-4 mr-1" />
                              Manage Links
                            </Link>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setSelectedDep(dep); setIsDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Add Dependency Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Dependency</DialogTitle>
            <DialogDescription>
              Create a parent-child relationship between reference lists.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddDependency} className="space-y-4 mt-4">
            <div>
              <Label>Relationship</Label>
              <Select value={addForm.role} onValueChange={(v) => setAddForm(prev => ({ ...prev, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent">This list is parent of...</SelectItem>
                  <SelectItem value="child">This list is child of...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{addForm.role === 'parent' ? 'Child List' : 'Parent List'}</Label>
              <Select value={addForm.list_id} onValueChange={(v) => setAddForm(prev => ({ ...prev, list_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a list..." />
                </SelectTrigger>
                <SelectContent>
                  {allLists.map(list => (
                    <SelectItem key={list.id} value={String(list.id)}>
                      {list.list_name} ({list.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={addForm.description}
                onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Country determines available ports"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !addForm.list_id}>
                {isSubmitting ? 'Adding...' : 'Add Dependency'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Dependency</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the dependency between "{selectedDep?.parent_list_name}" and "{selectedDep?.child_list_name}" and all associated value links. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedDep(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDependency} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
